import { useSelector } from 'react-redux';

import {
  getReportingAnchorPeriod,
  getReportingWindow,
  getTimeFilter,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { resolveReportingPeriod, getCurrentMonthYyyyMm } from 'utils/reportingPeriodUtils';

import type { ReportingWindow } from 'utils/reportingPeriodUtils';

export interface InstrumentDates {
  /** Dashboard-level anchor month in YYYY-MM format, never empty. */
  anchorPeriod: string;
  /** Three-letter FY start month abbreviation from the model (e.g. "Jan", "Apr"). */
  fyStartMonth: string;
  /**
   * The global reporting period resolved from anchor + window + FY start month.
   *
   * Must be sent as `reporting_period` in every single-instrument data API call
   * (POST /instrument/ and PUT /instrument/ with data_values=true).  The backend
   * uses this to set the correct window for KPI and Variance instruments whose
   * `reporting_window_mode` is "FOLLOW_GLOBAL" ("Auto from dashboard").  Without
   * it the backend has no global context and falls back to `start_date`/`end_date`
   * (the display timescale), producing a multi-year sum instead of the single
   * reporting-period value.
   */
  globalReportingPeriod: { start: string; end: string };
  /**
   * Resolves the effective start/end dates for the given widget.
   *
   * When the widget has a per-instrument window override (`reporting_window_mode = 'OVERRIDE'`),
   * the override is resolved against the dashboard anchor period.  Otherwise the
   * dashboard display timescale (`timeFilter`) is returned as the fallback.
   *
   * Accepts the widget explicitly so it works correctly when called with a
   * freshly-mutated widget before Redux state has caught up (e.g. immediately
   * after the user selects a new time window in the Setup panel).
   */
  resolveEffectiveDates: (widgetData: any) => { start: string; end: string };
  /**
   * Builds a complete request body for single-instrument data-preview API calls.
   *
   * This is the single source of truth for all instrument editor preview requests
   * (POST /instrument/ and PUT /instrument/ with data_values=true).  It guarantees
   * that every call includes:
   *   - Correctly resolved start_date / end_date (respects per-instrument override)
   *   - reporting_period with the real global window dates (not empty-string legacy)
   *   - anchor_period and fy_start_month for backend defence-in-depth
   *   - save: false (preview — no DB write)
   *
   * Usage:
   *   dispatch(updateInstrument({ request_body: buildPreviewPayload(widget, {
   *     scenario_id: selectedDashboardScenario?.id,
   *     compare_scenario: compareDashboardScenario?.id,
   *     instrument_indicators: updatedList,
   *   })}))
   *
   * Any field in `overrides` takes precedence over the base widget values.
   * Pass `data_values: false` in overrides to suppress data fetching (e.g. metadata-only saves).
   * Pass `save: true` in overrides for operations that should also persist to the DB.
   */
  buildPreviewPayload: (widget: any, overrides?: Record<string, any>) => Record<string, any>;
}

/**
 * Provides anchor-aware date resolution for instrument editor API requests.
 *
 * Centralises reading the dashboard reporting anchor, FY start month, global
 * reporting period, and per-instrument override resolution so that all
 * instrument editor components share identical logic through one source of truth.
 *
 * @example
 * const { anchorPeriod, fyStartMonth, globalReportingPeriod, resolveEffectiveDates } = useInstrumentDates();
 * const { start, end } = resolveEffectiveDates(editData);
 * // pass globalReportingPeriod as reporting_period in every data_values API call
 */
const useInstrumentDates = (): InstrumentDates => {
  // Use || so an empty-string state also falls back to the current month,
  // matching the same defensive pattern used in fetchAllDataValues.
  const anchorPeriod: string = useSelector(getReportingAnchorPeriod) || getCurrentMonthYyyyMm();
  const reportingWindow: ReportingWindow =
    (useSelector(getReportingWindow) as ReportingWindow) || 'SINGLE';
  const model = useSelector(getBlocks);
  const fyStartMonth: string = (model?.time_properties?.fy_start_month as string) || 'Jan';
  const timeFilter = useSelector(getTimeFilter);

  // Resolve the global reporting period once; used as `reporting_period` in all
  // single-instrument preview calls so the backend knows the correct window for
  // "Auto from dashboard" instruments regardless of their display timescale.
  const globalReportingPeriod = resolveReportingPeriod(anchorPeriod, reportingWindow, fyStartMonth);

  const resolveEffectiveDates = (widgetData: any): { start: string; end: string } => {
    const windowMode = widgetData?.formatting?.reporting_window_mode;
    const windowOverride = widgetData?.formatting?.reporting_window_override as ReportingWindow;

    if (windowMode === 'OVERRIDE' && windowOverride && anchorPeriod) {
      const { start, end } = resolveReportingPeriod(anchorPeriod, windowOverride, fyStartMonth);
      if (start && end) return { start, end };
    }

    return { start: timeFilter?.start || '', end: timeFilter?.end || '' };
  };

  const buildPreviewPayload = (
    widget: any,
    overrides: Record<string, any> = {},
  ): Record<string, any> => {
    // Merge first so resolveEffectiveDates picks up any formatting changes in overrides.
    const merged = { ...widget, ...overrides };
    const { start, end } = resolveEffectiveDates(merged);
    return {
      ...merged,
      save: overrides.save ?? false,
      data_values: overrides.data_values ?? true,
      start_date: start,
      end_date: end,
      reporting_period: globalReportingPeriod,
      anchor_period: anchorPeriod,
      fy_start_month: fyStartMonth,
    };
  };

  return {
    anchorPeriod,
    fyStartMonth,
    globalReportingPeriod,
    resolveEffectiveDates,
    buildPreviewPayload,
  };
};

export default useInstrumentDates;
